import {
  ACHIEVEMENT_KEYS,
  confirmationMatches,
  deletionConfirmation,
  EXERCISE_MINUTE_OPTIONS,
  PRIVACY_URL,
  UnsupportedPhotoFormatError,
} from '@echo/core';
import {
  setExerciseMinutes,
  setLargeText,
  useDeleteAccount,
  useExerciseMinutes,
  useExerciseMinutesSaveState,
  useLargeText,
  useMe,
  useMyGroups,
  useMyStats,
  useSessionManager,
  useSetProfilePhoto,
} from '@echo/core/react';
import { LOCALE_INFO } from '@echo/i18n';
import { useLocale } from '@echo/i18n/react';
import { Avatar, Button, ListRow, Sheet, Switch, TextField } from '@echo/ui-web';
import { useLingui } from '@lingui/react';
import { Trans, useLingui as useLinguiMacro } from '@lingui/react/macro';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  ALargeSmall,
  BellRing,
  BookOpenText,
  Camera,
  ChevronRight,
  CircleHelp,
  Clock4,
  Globe,
  Images,
  LogOut,
  OctagonX,
  ScrollText,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { AccessGuidance } from '@/components/access-guidance';
import { AchievementBadge, useAchievementLabels } from '@/components/achievement-badge';
import { ToolbarBack } from '@/components/empty-state-frame';
import { OverviewCard } from '@/components/overview-card';
import { PickerSheet } from '@/components/picker-sheet';
import { WebcamSheet } from '@/components/webcam-sheet';
import { useAuthorizedImageUrl } from '@/lib/authorized-image';
import { useTakePhoto } from '@/lib/camera';
import { heicAsJpeg } from '@/lib/heic';
import { formatReminderTime } from '@/lib/reminder-copy';
import { readStoredReminder } from '@/lib/reminder-storage';

export const Route = createFileRoute('/_app/account')({ component: AccountPage });

const AVATAR = 143;

function initialsOf(name: string): string {
  const [first = '', last = ''] = name.trim().split(/\s+/);
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

/**
 * Account: who the person is with the profile-picture panel beside them,
 * their numbers, achievements, preferences and the account actions. Numbers and achievements
 * come from their own sessions; the language and the reminder are kept in this browser, the
 * exercise duration on their account.
 */
function AccountPage() {
  const { t } = useLinguiMacro();
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const mine = useMyGroups();
  // One group opens directly; the list is for none or several (Figma "Selector" note).
  const openMyGroups = () => {
    const [only] = mine.data ?? [];
    if (mine.data?.length === 1 && only) {
      void navigate({ to: '/account/groups/$id', params: { id: only.actorUuid } });
    } else {
      void navigate({ to: '/account/groups' });
    }
  };
  const sessionManager = useSessionManager();
  const me = useMe();
  const stats = useMyStats();
  const { locale, options, setLocale } = useLocale();
  const [languageOpen, setLanguageOpen] = useState(false);
  const largeText = useLargeText();
  const achievementLabel = useAchievementLabels();
  const exerciseMinutes = useExerciseMinutes();
  const [durationOpen, setDurationOpen] = useState(false);
  const exerciseSave = useExerciseMinutesSaveState();
  // Read on every visit: the Reminders page may have changed it.
  const [reminder] = useState(readStoredReminder);
  const deleteAccount = useDeleteAccount();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const [confirmation, setConfirmation] = useState('');
  const confirmBy = me.data ? deletionConfirmation(me.data) : null;
  const confirmed = !!me.data && confirmationMatches(me.data, confirmation);
  const openDelete = () => {
    setConfirmation('');
    deleteAccount.reset();
    setDeleteOpen(true);
  };

  const name = me.data?.name ?? '';
  const photo = useAuthorizedImageUrl(me.data?.profileImage?.medium?.url ?? undefined);
  const memberSince = stats.data?.memberSince
    ? i18n.date(new Date(`${stats.data.memberSince}T12:00:00Z`), {
        month: 'long',
        year: 'numeric',
        timeZone: 'UTC',
      })
    : undefined;
  const earned = new Set(stats.data?.achievements ?? []);
  const chevron = <ChevronRight size={24} className="text-icon-medium" aria-hidden />;
  const icon = (Icon: typeof BellRing) => (
    <Icon size={24} className="text-icon-strong" aria-hidden />
  );

  // Change profile picture: a file from the computer, or the camera — the phone's own from
  // the file input, the webcam in a sheet on a laptop — then Core's profile image route, the
  // same photo every OnEarly product shows for this person.
  const setPhoto = useSetProfilePhoto();
  const libraryInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const [photoNotice, setPhotoNotice] = useState<'failed' | 'format' | null>(null);
  // The phone's camera app, or the webcam on a laptop; the camera guidance takes the page
  // over when the browser has refused the camera.
  const camera = useTakePhoto(cameraInput);
  const objectUrl = useRef<string | undefined>(undefined);
  useEffect(
    () => () => {
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    },
    [],
  );
  const [converting, setConverting] = useState(false);
  const photoBusy = converting || setPhoto.isPending;
  const changePhoto = async (picked: File | undefined) => {
    if (!picked) return;
    setPhotoNotice(null);
    setConverting(true);
    const file = await heicAsJpeg(picked)
      .catch((error: unknown) => {
        setPhotoNotice(error instanceof UnsupportedPhotoFormatError ? 'format' : 'failed');
      })
      .finally(() => setConverting(false));
    if (!file) return;
    if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    objectUrl.current = URL.createObjectURL(file);
    setPhoto.mutate(
      {
        uri: objectUrl.current,
        name: file.name || 'profile.jpg',
        mimeType: file.type || 'image/jpeg',
      },
      {
        onError: (error) =>
          setPhotoNotice(error instanceof UnsupportedPhotoFormatError ? 'format' : 'failed'),
      },
    );
  };
  if (camera.guidance) {
    const { onNotNow } = camera.guidance;
    return (
      <>
        <ToolbarBack
          render={({ className, children }) => (
            <button type="button" className={className} onClick={onNotNow}>
              {children}
            </button>
          )}
        />
        <AccessGuidance kind="camera" {...camera.guidance} />
      </>
    );
  }

  return (
    <>
      <h1 className="text-display-sm font-bold">
        <Trans>Account</Trans>
      </h1>
      <div className="flex flex-col gap-section-sm">
        <section className="flex flex-col gap-content-lg rounded-xl bg-surface px-content-sm py-content-md sm:flex-row sm:items-stretch">
          <div className="flex flex-1 flex-col items-center gap-internal-md p-internal-lg">
            <Avatar
              size={AVATAR}
              src={photo}
              initials={initialsOf(name)}
              tone="olive"
              label={name}
            />
            <div className="flex flex-col items-center text-center">
              <span className="text-headline-lg font-normal">{name}</span>
              {memberSince ? (
                <span className="text-body-sm text-on-surface-medium">
                  <Trans>Member since {memberSince}</Trans>
                </span>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col justify-center gap-content-sm rounded-lg p-internal-lg sm:w-[335px]">
            <div className="flex flex-col gap-internal-sm">
              <h2 className="text-title-lg">
                <Trans>Change profile picture</Trans>
              </h2>
              <p className="text-body-md text-on-surface-medium">
                <Trans>
                  You can select an existing photo from your computer or capture a new one using
                  your webcam.
                </Trans>
              </p>
            </div>
            <input
              ref={libraryInput}
              type="file"
              accept="image/*"
              className="sr-only"
              tabIndex={-1}
              onChange={(event) => {
                void changePhoto(event.currentTarget.files?.[0]);
                event.currentTarget.value = '';
              }}
            />
            <input
              ref={cameraInput}
              type="file"
              accept="image/*"
              capture="user"
              className="sr-only"
              tabIndex={-1}
              onChange={(event) => {
                void changePhoto(event.currentTarget.files?.[0]);
                event.currentTarget.value = '';
              }}
            />
            <WebcamSheet
              {...camera.webcam}
              title={t`Take a new photo`}
              facing="user"
              onCapture={changePhoto}
            />
            <Button
              variant="secondary"
              width="full"
              onClick={() => libraryInput.current?.click()}
              disabled={photoBusy}
              aria-busy={photoBusy}
            >
              <Images size={24} aria-hidden />
              {photoBusy ? t`Updating photo…` : t`Browse files`}
            </Button>
            <Button
              variant="secondary"
              width="full"
              onClick={() => void camera.takePhoto()}
              disabled={photoBusy}
            >
              <Camera size={24} aria-hidden />
              <Trans>Take a new photo</Trans>
            </Button>
            {photoNotice ? (
              <p
                role="alert"
                className="flex items-start gap-internal-md rounded-md bg-status-error-tint py-internal-lg pr-internal-lg pl-content-sm text-body-md text-on-surface-medium"
              >
                <OctagonX size={24} className="shrink-0 text-status-error" aria-hidden />
                {photoNotice === 'format' ? (
                  <span className="flex flex-col gap-internal-sm">
                    <span className="font-bold text-on-surface">
                      <Trans>Please choose a different image</Trans>
                    </span>
                    <Trans>Profile pictures need to be in JPG or PNG format.</Trans>
                  </span>
                ) : (
                  <Trans>
                    Your photo couldn't be updated. Check your connection and try again.
                  </Trans>
                )}
              </p>
            ) : null}
          </div>
        </section>

        <OverviewCard stats={stats.data} />

        <section
          className="flex flex-col gap-content-sm rounded-xl border border-border-weak bg-surface px-content-sm py-content-md"
          aria-labelledby="achievements-title"
        >
          <div>
            <h2 id="achievements-title" className="text-title-lg">
              <Trans>Your achievements</Trans>
            </h2>
            <p className="text-body-md text-on-surface-medium">
              <Trans>Complete exercises to unlock achievements.</Trans>
            </p>
          </div>
          <ul className="grid grid-cols-2 gap-content-sm sm:grid-cols-4">
            {ACHIEVEMENT_KEYS.map((key) => (
              <li key={key}>
                <AchievementBadge
                  achievementKey={key}
                  earned={earned.has(key)}
                  label={achievementLabel[key]}
                />
              </li>
            ))}
          </ul>
        </section>

        <Section title={<Trans>Preferences</Trans>}>
          <ListRow
            icon={icon(BellRing)}
            label={t`Reminders`}
            value={reminder.enabled ? formatReminderTime(i18n, reminder) : t`Off`}
            trailing={chevron}
            onClick={() => void navigate({ to: '/account/reminders' })}
          />
          <ListRow
            icon={icon(Clock4)}
            label={t`Exercise duration`}
            value={t`${exerciseMinutes} min`}
            trailing={chevron}
            onClick={() => setDurationOpen(true)}
          />
          {exerciseSave.refused ? (
            <div className="flex flex-col items-start gap-internal-md py-internal-lg">
              <p
                role="status"
                className="flex w-full items-start gap-internal-md rounded-md bg-status-error-tint py-internal-lg pr-internal-lg pl-content-sm text-body-md text-on-surface-medium"
              >
                <OctagonX size={24} className="shrink-0 text-status-error" aria-hidden />
                <Trans>
                  Couldn't save your exercise duration. Check your connection and try again.
                </Trans>
              </p>
              <Button variant="secondary" onClick={exerciseSave.retry}>
                {t`Try again`}
              </Button>
            </div>
          ) : exerciseSave.pending ? (
            <p
              role="status"
              className="px-internal-lg py-internal-lg text-body-md text-on-surface-medium"
            >
              <Trans>Will save when you're back online</Trans>
            </p>
          ) : null}
          <ListRow
            icon={icon(ALargeSmall)}
            label={t`Large text`}
            trailing={
              <Switch checked={largeText} onCheckedChange={setLargeText} label={t`Large text`} />
            }
          />
          <ListRow
            icon={icon(Globe)}
            label={t`Language`}
            value={LOCALE_INFO[locale].nativeName}
            trailing={chevron}
            onClick={() => setLanguageOpen(true)}
          />
        </Section>

        <Section title={<Trans>Account</Trans>}>
          {/* Share progress has no destination yet. */}
          <ListRow
            icon={icon(Users)}
            label={t`My groups`}
            trailing={chevron}
            onClick={openMyGroups}
          />
          <ListRow
            icon={icon(CircleHelp)}
            label={t`Help & support`}
            trailing={chevron}
            onClick={() => void navigate({ to: '/account/help' })}
          />
          <ListRow
            icon={icon(BookOpenText)}
            label={t`About StrongerMemory`}
            trailing={chevron}
            onClick={() => void navigate({ to: '/account/about' })}
          />
          {/* Goodwin Living's policy, the same page sign-in links to; a new tab keeps the app. */}
          <ListRow
            icon={icon(ScrollText)}
            label={t`Privacy Policy`}
            trailing={chevron}
            onClick={() => window.open(PRIVACY_URL, '_blank', 'noopener,noreferrer')}
          />
          {/* Like the rows above it: icon first, chevron last; Sign out stays last. */}
          <ListRow
            icon={icon(Trash2)}
            label={t`Delete account`}
            trailing={chevron}
            onClick={openDelete}
          />
          {/* Signing out unmounts the shell (_app.tsx) and lands on the sign-in page. */}
          <ListRow
            label={t`Sign out`}
            trailing={<LogOut size={24} className="text-icon-strong" aria-hidden />}
            onClick={() => setSignOutOpen(true)}
          />
        </Section>
      </div>

      <PickerSheet
        open={languageOpen}
        title={t`Language`}
        options={options.map((option) => ({ value: option.tag, label: option.nativeName }))}
        selected={locale}
        onClose={() => setLanguageOpen(false)}
        onDone={(next) => {
          setLanguageOpen(false);
          if (next !== locale) setLocale(next);
        }}
      />
      <PickerSheet
        open={durationOpen}
        title={t`Exercise duration`}
        options={EXERCISE_MINUTE_OPTIONS.map((minutes) => ({
          value: minutes,
          label: t`${minutes} min`,
        }))}
        selected={exerciseMinutes}
        onClose={() => setDurationOpen(false)}
        onDone={(minutes) => {
          setDurationOpen(false);
          setExerciseMinutes(minutes);
        }}
      />
      {/* A successful delete signs out, which lands on the sign-in page like Sign out. */}
      <Sheet
        open={deleteOpen}
        title={t`Delete account`}
        closeLabel={t`Close`}
        closeIcon={<X size={24} aria-hidden />}
        onClose={() => setDeleteOpen(false)}
      >
        <div className="flex flex-col gap-content-sm px-content-sm pb-content-lg">
          <p className="text-body-md text-on-surface-medium">
            <Trans>
              Deleting your account permanently removes your StrongerMemory account, your progress,
              achievements and profile. This can't be undone, and your data can't be recovered.
            </Trans>
          </p>
          <TextField
            label={
              confirmBy === 'phone'
                ? t`Please type in your mobile number to confirm.`
                : t`Please type in your email to confirm.`
            }
            value={confirmation}
            onChange={(e) => setConfirmation(e.currentTarget.value)}
            type={confirmBy === 'phone' ? 'tel' : 'email'}
            autoComplete="off"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {deleteAccount.isError ? (
            <p
              role="alert"
              className="flex items-start gap-internal-md rounded-md bg-status-error-tint py-internal-lg pr-internal-lg pl-content-sm text-body-md text-on-surface-medium"
            >
              <OctagonX size={24} className="shrink-0 text-status-error" aria-hidden />
              <Trans>Your account couldn't be deleted. Check your connection and try again.</Trans>
            </p>
          ) : null}
        </div>
        <div className="flex flex-col gap-internal-md px-content-sm pb-section-sm">
          <Button
            variant="destructive"
            width="full"
            loading={deleteAccount.isPending}
            disabled={!confirmed}
            onClick={() => deleteAccount.mutate()}
          >
            <Trans>Permanently delete account</Trans>
          </Button>
          <Button variant="secondary" width="full" onClick={() => setDeleteOpen(false)}>
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Sheet>
      <Sheet
        open={signOutOpen}
        title={t`Sign out?`}
        closeLabel={t`Close`}
        closeIcon={<X size={24} aria-hidden />}
        onClose={() => setSignOutOpen(false)}
      >
        <div className="flex flex-col gap-content-sm px-content-sm pb-content-lg">
          <p className="text-body-md text-on-surface-medium">
            <Trans>You'll need to sign in again to use StrongerMemory.</Trans>
          </p>
        </div>
        <div className="flex flex-col gap-internal-md px-content-sm pb-section-sm">
          <Button variant="primary" width="full" onClick={() => void sessionManager.signOut()}>
            <Trans>Sign out</Trans>
          </Button>
          <Button variant="secondary" width="full" onClick={() => setSignOutOpen(false)}>
            <Trans>Cancel</Trans>
          </Button>
        </div>
      </Sheet>
      {/* The rows above are links to pages; a keyboard user also gets them as plain links. */}
      <Link to="/account/reminders" className="sr-only">
        <Trans>Reminders</Trans>
      </Link>
    </>
  );
}

function Section({ title, children }: { title: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-content-sm rounded-xl bg-surface px-content-sm py-content-md">
      <h2 className="text-title-lg">{title}</h2>
      <div className="flex flex-col">{children}</div>
    </section>
  );
}
